import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Autocomplete, TextField, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import ProductionDTO from '@src/adapters/dto/production/ProductionDTO';
import { useSaveProductionLazy } from '@src/adapters/hooks/production/useSaveProduction';
import { useCallback, useEffect, useState } from 'react';
import ProductionCompanyDTO from '@src/adapters/dto/productionCompany/ProductionCompanyDTO';
import useFindProductionCompaniesListLazy from '@src/adapters/hooks/productionCompany/useFindProductionCompaniesList';

interface Props {
  close(production: ProductionDTO | undefined): void;
  open: boolean;
}

function ProductionAdd({ close, open }: Props) {
  const { execute: save } = useSaveProductionLazy();
  const { execute: getList, loading } = useFindProductionCompaniesListLazy();
  const [companyOpen, setCompanyOpen] = useState(false);
  const [prodComps, setProdComps] = useState<ProductionCompanyDTO[]>([]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ProductionDTO>();

  const findCompanies = useCallback(() => {
    getList().then((result) => {
      setProdComps(result.items);
    });
  }, [getList]);

  useEffect(() => {
    findCompanies();
  }, [findCompanies]);

  const onSubmit = async (data: ProductionDTO) => {
    const result = await save({
      name: data.name,
      productionCompanyId: data.productionCompanyId,
    });
    if (result.production) {
      reset();
      close(result.production);
    }
  };

  const handleClose = () => {
    reset();
    close(undefined);
  };

  return (
    <Dialog
      open={open}
      aria-labelledby="Note Details"
      aria-describedby="Note Details"
      fullWidth={true}
      maxWidth={'md'}>
      <DialogTitle
        id="alert-dialog-title"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}>
        <Typography variant="h6">Add Production</Typography>
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}>
          <Controller
            name="name"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Name"
                sx={{ width: '100%', marginTop: '20px', marginBottom: '20px' }}
                error={!!errors.name}
              />
            )}
          />
          {errors.name && errors.name.message}
          <Controller
            name="productionCompanyId"
            control={control}
            rules={{ required: true }}
            defaultValue={undefined}
            render={({ field }) => (
              <Autocomplete
                fullWidth
                open={companyOpen}
                onOpen={() => {
                  setCompanyOpen(true);
                }}
                onClose={() => {
                  setCompanyOpen(false);
                }}
                onChange={(event, value) => {
                  field.onChange(value ? value.id : '');
                }}
                value={
                  prodComps.find((option) => option.id === field.value) || null
                }
                isOptionEqualToValue={(option, value) =>
                  option.id === value?.id
                }
                getOptionLabel={(option) => option.name || ''}
                options={prodComps}
                loading={loading}
                filterSelectedOptions
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Production Company"
                    variant="standard"
                    error={!!errors.productionCompanyId}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: <>{params.InputProps.endAdornment}</>,
                      },
                    }}
                  />
                )}
              />
            )}
          />
        </DialogContent>
        <DialogActions sx={{ flex: '1', marginRight: '8px' }}>
          <Button
            onClick={handleClose}
            color="primary"
            variant="outlined"
            sx={{ flex: '1', marginLeft: '8px' }}>
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            sx={{ flex: '1', marginLeft: '8px' }}>
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default ProductionAdd;
